import { enableProdMode, ApplicationRef } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { enableDebugTools } from '@angular/platform-browser';
import { environment } from '@environment/environment';

import { AppModule } from './app/app.module';

// Local-dev login mask: the hecate OAuth server (AUTH_SERVER_URL) is origin-gated
// and 504s for the http://localhost origin (it only serves the deployed SPA
// origins), and the legacy /login fallback was retired (410 Gone). So when serving
// locally we keep the hecate flow but route its /oauth/token calls through the
// ng-serve dev proxy: blanking AUTH_SERVER_URL makes them same-origin
// (/oauth/token), and proxy.conf.js forwards them to dev-auth server-side with
// changeOrigin, bypassing the origin gate.
//
// Gated on LOCAL_HECATE_PROXY (only set in environment.ts, which backs
// `ng serve -c dev` — the one config that enables the proxy) AND a localhost
// hostname. So it never fires on a deployed domain, nor under
// `ng serve -c staging|production|cypress` (whose env files lack the flag), which
// would otherwise blank AUTH_SERVER_URL with no proxy behind it.
const HECATE_LOCALHOSTS = ['localhost', '127.0.0.1', '0.0.0.0'];
if (
	(environment as { LOCAL_HECATE_PROXY?: boolean }).LOCAL_HECATE_PROXY &&
	HECATE_LOCALHOSTS.includes(window.location.hostname)
) {
	environment.AUTH_SERVER_URL = '';
	// eslint-disable-next-line no-console
	console.info('[c4] localhost — hecate /oauth/token routed through the ng-serve proxy.');
}

if (environment.production) {
	enableProdMode();
}

platformBrowserDynamic()
	.bootstrapModule(AppModule, { ngZoneEventCoalescing: true })
	.then((module) => {
		const appRef = module.injector.get(ApplicationRef);
		enableDebugTools(appRef.components[0]);
	})
	.catch((err) => console.log(err));

if ('serviceWorker' in navigator) {
	navigator.serviceWorker
		.register('/firebase-messaging-sw.js')
		.then(function () {
			console.log('Firebase Worker Registered');
		})
		.catch(function (err) {
			console.log('Service Worker registration failed: ', err);
		});
}

// load segment bundle
/*
 * script to load segment library from assets folder
 * to get these files, run app locally and find all files requested by segment appling key, ~ 4 files are:
 * analytics.min.js (this file in requests two other scripts in)
 * 130.bundle.55742ac9337d9e12bdd6.js
 * ajs-destination.bundle.36b90a11867ae217be52.js
 * settings
 * in analytics.min.js file update url http://apisegment.cargochief.com/v1 with the proxt url (for posting segment event)
 * in analytics.min.js file update url for all the files requested by script to load from local
 */
const script = document.createElement('script');
script.innerHTML =
	`
	!function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="` +
	`${environment.production ? 'assets/seg/prod/a-min.js' : 'assets/seg/dev/a-min.js'}` +
	`";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="${environment.SEGMENT_KEY}";analytics._cdn="${environment.SEGMENT_PROXY}";analytics.SNIPPET_VERSION="4.15.2";
	analytics.load("${environment.SEGMENT_KEY}");
	}}();
`;
document.head.appendChild(script);

// Inject PWA manifest only on mobile to prevent the browser install icon (⊕) appearing on desktop.
// Apple meta tags remain static in index.html since iOS Safari reads them before JS runs.
(function injectManifestOnMobile() {
	const ua = navigator.userAgent.toLowerCase();
	const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(ua);
	const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
	const isMobileWidth = window.innerWidth <= 768;
	if (isMobileUA || (isTouchDevice && isMobileWidth)) {
		const link = document.createElement('link');
		link.rel = 'manifest';
		link.href = '/manifest.json';
		document.head.appendChild(link);
	}
})();

// PWA Setup - Register ngsw service worker for PWA support.
// iOS Safari 16.4+ requires a registered service worker to treat "Add to Home Screen" as a PWA (not bookmark).
// ENABLE_PWA flag controls the install prompt UI only, not the service worker registration.
// Manifest and Apple meta tags are in index.html (must be static for iOS Safari).
if ('serviceWorker' in navigator) {
	// Clean up stale ngsw service workers and their caches.
	// Called in dev mode (ngsw-worker.js doesn't exist) or when server is down (fetch fails).
	function cleanupStaleNgswWorkers() {
		navigator.serviceWorker.getRegistrations().then((registrations) => {
			registrations.forEach((reg) => {
				if (reg.active?.scriptURL?.includes('ngsw-worker')) {
					reg.unregister().then(() => {
						console.log('Stale ngsw service worker unregistered');
					});
				}
			});
		});
		if ('caches' in window) {
			caches.keys().then((cacheNames) => {
				cacheNames.forEach((name) => {
					if (name.startsWith('ngsw:')) {
						caches.delete(name);
					}
				});
			});
		}
	}

	window.addEventListener('load', () => {
		// Check if ngsw-worker.js actually exists (only in production builds).
		// ng serve returns index.html for missing files (SPA fallback), so verify content-type.
		fetch('/ngsw-worker.js?ngsw-bypass=true')
			.then((response) => {
				const contentType = response.headers.get('content-type') || '';
				if (response.ok && contentType.includes('javascript')) {
					// Production build: ngsw-worker.js exists, register it.
					// register() already checks for SW updates by comparing the script byte-by-byte.
					navigator.serviceWorker
						.register('/ngsw-worker.js')
						.then(() => {
							console.log('PWA Service Worker registered successfully');
						})
						.catch((err) => {
							console.warn('PWA Service Worker registration failed:', err);
						});
				} else {
					// Dev mode (ng serve): ngsw-worker.js doesn't exist
					cleanupStaleNgswWorkers();
				}
			})
			.catch(() => {
				// Server is down or network error — clean up stale workers
				cleanupStaleNgswWorkers();
			});
	});
}
